import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaCheckCircle, FaChartLine, FaCheck, FaMoneyBillWave, FaFileInvoiceDollar,
    FaCreditCard, FaUserShield, FaBuilding, FaMobileAlt, FaArrowRight
} from 'react-icons/fa';
import expanncceImage from '../../assets/expanncce.png';

const ExpenseManagementPage = () => {
    // FAQ State
    const [openFaq, setOpenFaq] = useState(null);
    const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

    return (
        <div className="bg-white font-sans text-slate-900 pt-20">
            {/* 1. Product Intro Section (No Hero Image Background) */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 font-bold text-xs uppercase tracking-wider rounded-full mb-6">
                            Finance & Operations
                        </div>
                        <h1 className="text-4xl md:text-5xl font-extrabold mb-6 text-slate-900 leading-tight">
                            Smart <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Expense Management</span>
                        </h1>
                        <p className="text-lg text-slate-600 mb-8 leading-relaxed font-medium">
                            Stop chasing receipts. Our automated platform empowers your team to report expenses in seconds and gives finance total control and visibility.
                        </p>

                        <div className="space-y-4 mb-8">
                            {[
                                "Real-time expense tracking & reporting",
                                "One-click approval workflows",
                                "Multi-currency & tax support",
                                "Seamless accounting integrations"
                            ].map((feature, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                                        <FaCheck className="text-white text-xs" />
                                    </div>
                                    <span className="text-slate-700 font-semibold">{feature}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <a href="#contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-lg hover:shadow-lg hover:to-indigo-700 transition-all flex items-center gap-2">
                                Request Demo <FaArrowRight />
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative"
                    >
                        <div className="bg-gradient-to-tr from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-2xl border border-blue-100/50">
                            <img
                                src={expanncceImage}
                                alt="Expense Management Dashboard"
                                className="w-full h-auto rounded-lg shadow-sm"
                            />
                        </div>
                        {/* Decorative blobs */}
                        <div className="absolute -z-10 top-1/2 -right-10 w-40 h-40 bg-blue-400/20 rounded-full blur-3xl"></div>
                        <div className="absolute -z-10 -bottom-10 -left-10 w-40 h-40 bg-indigo-400/20 rounded-full blur-3xl"></div>
                    </motion.div>
                </div>
            </div>

            {/* 2. Detailed Features with Icons */}
            <div className="bg-slate-50 py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Everything You Need</h2>
                        <p className="text-slate-600 max-w-2xl mx-auto text-lg">Simplify the entire expense lifecycle from swipe to reimbursement.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { icon: FaFileInvoiceDollar, title: "Smart Scanning", desc: "OCR technology automatically scans receipts and extracts data with 99% accuracy.", color: "text-blue-600", bg: "bg-blue-50" },
                            { icon: FaUserShield, title: "Policy Enforcement", desc: "Automatically flag or block expenses that violate company spending policies.", color: "text-indigo-600", bg: "bg-indigo-50" },
                            { icon: FaBuilding, title: "ERP Integration", desc: "Syncs perfectly with QuickBooks, Xero, NetSuite, SAP, and other major ERPs.", color: "text-purple-600", bg: "bg-purple-50" },
                            { icon: FaMobileAlt, title: "Mobile First", desc: "Submit, approve, and track expenses from anywhere with our iOS and Android apps.", color: "text-pink-600", bg: "bg-pink-50" },
                            { icon: FaChartLine, title: "Visual Analytics", desc: "Gain insights into spending patterns by category, department, or project.", color: "text-orange-600", bg: "bg-orange-50" },
                            { icon: FaCreditCard, title: "Card Management", desc: "Issue virtual and physical corporate cards with pre-set spending limits.", color: "text-teal-600", bg: "bg-teal-50" }
                        ].map((item, idx) => (
                            <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 group">
                                <div className={`w-14 h-14 ${item.bg} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                    <item.icon className={`${item.color} text-2xl`} />
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-slate-900">{item.title}</h3>
                                <p className="text-slate-600 leading-relaxed font-medium">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 3. Colorful Value Prop Section */}
            <div className="py-20 bg-gradient-to-br from-indigo-900 to-blue-900 text-white overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Finance Teams Love Us</h2>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                        <span className="text-2xl font-bold text-blue-300">1</span>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-2">Save 40% Administrative Time</h4>
                                        <p className="text-blue-100">No more manual data entry. Automation handles the heavy lifting.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                        <span className="text-2xl font-bold text-blue-300">2</span>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-2">Reimburse 5x Faster</h4>
                                        <p className="text-blue-100">Keep employees happy with next-day reimbursements directly to their bank.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                                        <span className="text-2xl font-bold text-blue-300">3</span>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-2">100% Audit Readiness</h4>
                                        <p className="text-blue-100">Every record is digitally stored, compliant, and ready for review.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
                            <h3 className="text-2xl font-bold mb-6 border-b border-white/10 pb-4">Monthly Savings Calculator</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center text-blue-200 text-sm">
                                    <span>Team Size</span>
                                    <span>50 Employees</span>
                                </div>
                                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                                    <div className="w-1/3 h-full bg-blue-400"></div>
                                </div>
                                <div className="pt-6">
                                    <div className="text-sm font-bold text-blue-200 uppercase tracking-widest mb-1">Estimated Annual Savings</div>
                                    <div className="text-5xl font-bold text-white">$45,000</div>
                                </div>
                                <p className="text-xs text-blue-300 mt-4">*Based on industry average processing costs.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. FAQ Section */}
            <div className="py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-center mb-12 text-slate-900">Frequently Asked Questions</h2>
                    <div className="space-y-4">
                        {[
                            { q: "Is it compatible with my bank?", a: "Yes, we support feeds from over 12,000 financial institutions globally for automatic transaction matching." },
                            { q: "Can I set different policies for different teams?", a: "Absolutely. You can configure granular spending limits and categories for departments, levels, or specific projects." },
                            { q: "How secure is my financial data?", a: "We use bank-grade 256-bit encryption and are SOC 2 Type II compliant to ensure your data is always safe." },
                            { q: "Do you offer a free trial?", a: "Yes, you can try the full platform for 14 days with no credit card required." }
                        ].map((faq, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => toggleFaq(idx)}
                                    className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                >
                                    <span className="font-bold text-slate-800">{faq.q}</span>
                                    <span className={`text-blue-600 font-bold text-xl transition-transform ${openFaq === idx ? 'rotate-45' : ''}`}>+</span>
                                </button>
                                {openFaq === idx && (
                                    <div className="px-6 py-4 bg-white text-slate-600 font-medium leading-relaxed">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 5. Contact / CTA */}
            <div id="contact" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="bg-blue-900 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
                        <div className="md:w-1/2 p-12 flex flex-col justify-center text-white">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">Start Saving Today</h2>
                            <p className="text-blue-200 text-lg mb-8">Join over 5,000 fast-growing companies that trust us to manage their spend.</p>
                            <ul className="space-y-3 mb-8">
                                <li className="flex items-center gap-3"><FaCheckCircle className="text-blue-400" /> <span>No setup fees</span></li>
                                <li className="flex items-center gap-3"><FaCheckCircle className="text-blue-400" /> <span>Cancel anytime</span></li>
                                <li className="flex items-center gap-3"><FaCheckCircle className="text-blue-400" /> <span>24/7 Support</span></li>
                            </ul>
                        </div>
                        <div className="md:w-1/2 bg-white p-12">
                            <form className="space-y-5">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Work Email</label>
                                    <input type="email" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 font-medium" placeholder="name@company.com" />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Company Name</label>
                                    <input type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 font-medium" placeholder="Acme Inc." />
                                </div>
                                <button className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-lg transition-all text-lg tracking-wide uppercase">
                                    Get Your Free Account
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseManagementPage;
